<template>
  <section class="mdx-white" ref="root" aria-label="Marketing Digital">
    <!-- HERO -->
    <header class="hero" ref="heroRef">
      <p class="hero__eyebrow">Marketing Digital</p>
      <h2 class="hero__title">
        Haz que te <span class="mark">encuentren</span>, te
        <span class="mark">elijan</span> y te <span class="mark">compren</span>.
      </h2>
      <p class="hero__sub">
        Formato claro, directo y sin vueltas. Lo que ya probamos y da resultado.
      </p>
    </header>

    <!-- LAYOUT -->
    <div class="stage">
      <!-- IZQ (sticky en desktop / encabezado simple en móvil) -->
      <aside class="sticky" ref="stickyRef" aria-live="polite">
        <div class="sticky__wrap">
          <h3 class="sticky__kicker">{{ currentKicker }}</h3>
          <h2 class="sticky__title">
            <span class="badge">{{ currentTitle }}</span>
          </h2>
          <p class="sticky__line">{{ currentLine }}</p>
          <ul class="sticky__proof">
            <li><strong>+120</strong> proyectos reales</li>
            <li>Soporte <strong>de verdad</strong></li>
          </ul>
        </div>
      </aside>

      <!-- DER (panels) -->
      <main class="panels" ref="panelsRef">
        <article
          v-for="(p, i) in panels"
          :key="p.key"
          class="panel"
          :class="`panel--${p.theme}`"
          :ref="el => panelEls[i] = el"
        >
          <div class="panel__content">
            <h4 class="panel__eyebrow">{{ p.eyebrow }}</h4>
            <h3 class="panel__title">{{ p.title }}</h3>
            <p class="panel__text">{{ p.text }}</p>
            <ul class="panel__bullets">
              <li v-for="(b, j) in p.bullets" :key="j">{{ b }}</li>
            </ul>

            <div class="panel__kpi" v-if="p.kpi">
              <div class="k">{{ p.kpi.value }}</div>
              <div class="klabel">{{ p.kpi.label }}</div>
            </div>
          </div>
        </article>
      </main>
    </div>

    <!-- WHY -->
    <section class="why" ref="whyRef" aria-label="Por qué es buena inversión">
      <h3 class="why__title">¿Por qué vale la pena?</h3>
      <div class="why__grid">
        <div class="why__card" v-for="w in why" :key="w.key">
          <h4>{{ w.title }}</h4>
          <p>{{ w.text }}</p>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'MarketingDigitalShowcaseWhite',
  setup () {
    const root = ref(null)
    const heroRef = ref(null)
    const stickyRef = ref(null)
    const panelsRef = ref(null)
    const panelEls = ref([])
    const whyRef = ref(null)

    const panels = ref([
      {
        key: 'seo',
        eyebrow: 'Ser visible',
        title: 'SEO que te sube en Google',
        text: 'Optimizamos tu web para que destaque en Google justo cuando tu cliente te busca.',
        bullets: [
          'Palabras clave locales que sí traen clientes',
          'Velocidad, indexación y estructura',
          'Títulos y descripciones que invitan a dar clic'
        ],
        theme: 'purple',
        kpi: { value: '3x', label: 'más visitas orgánicas' }
      },
      {
        key: 'ads',
        eyebrow: 'Traer demanda',
        title: 'Google Ads en el momento exacto',
        text: 'Pagas solo por clic. Mostramos tu oferta justo cuando la necesitan.',
        bullets: [
          'Llamadas o llegada directa a tu negocio',
          'Quitamos búsquedas que no venden',
          'Mejor uso del presupuesto para conseguir clientes'
        ],
        theme: 'blue',
        kpi: { value: '+40%', label: 'más consultas' }
      },
      {
        key: 'social',
        eyebrow: 'Ser recordado',
        title: 'Campañas en Facebook e Instagram',
        text: 'Anuncios que aparecen cuando tu cliente está listo, con enfoque en conexión y ventas.',
        bullets: [
          'Segmentación precisa',
          'Creatividades y copys que convierten',
          'Optimización continua para escalar'
        ],
        theme: 'teal'
      },
      {
        key: 'content',
        eyebrow: 'Educar y convertir',
        title: 'Contenido que atrae y convence',
        text: 'Explicamos lo que ofreces y resolvemos dudas para que confíen en ti.',
        bullets: [
          'Publicaciones claras y fáciles de entender',
          'Artículos, videos y guías útiles'
        ],
        theme: 'amber'
      },
      {
        key: 'site',
        eyebrow: 'Cerrar la venta',
        title: 'Más ventas para tu negocio',
        text: 'Página y redes pensadas para que te contacten y compren sin fricción.',
        bullets: [
          'Diseño que guía a la acción',
          'Botones de contacto visibles',
          'Carga rápida y SEO técnico'
        ],
        theme: 'green',
        kpi: { value: '+25%', label: 'más clientes cerrados' }
      }
    ])

    const why = ref([
      { key: '1', title: 'Medible', text: 'Sabes qué funciona y qué mejorar.' },
      { key: '2', title: 'Escalable', text: 'Empiezas pequeño y escalas con datos.' },
      { key: '3', title: 'Sencillo', text: 'Acciones concretas que venden.' }
    ])

    // Sticky dinámico
    const currentKicker = ref(panels.value[0].eyebrow)
    const currentTitle  = ref(panels.value[0].title)
    const currentLine   = ref(panels.value[0].text)
    const setStickyFromPanel = (i) => {
      const p = panels.value[i]; if (!p) return
      currentKicker.value = p.eyebrow
      currentTitle.value  = p.title
      currentLine.value   = p.text
    }

    let ctx
    onMounted(async () => {
      await nextTick()

      ctx = gsap.context(() => {
        // HERO
        gsap.from(heroRef.value.children, {
          autoAlpha: 0, y: 18, duration: 0.6, ease: 'power2.out', stagger: 0.08,
          scrollTrigger: { trigger: heroRef.value, start: 'top 85%', once: true }
        })

        panelEls.value = panelEls.value.filter(Boolean)
        gsap.set(panelEls.value, { autoAlpha: 0, y: 20 })

        // Desktop / tablets
        ScrollTrigger.matchMedia({
          '(min-width: 901px)': () => {
            ScrollTrigger.create({
              trigger: root.value,
              start: 'top top+=80',
              end: () => '+=' + (panelsRef.value.scrollHeight - window.innerHeight + 240),
              pin: stickyRef.value,
              pinSpacing: false,
              anticipatePin: 1
            })
            panelEls.value.forEach((el, i) => {
              gsap.timeline({
                scrollTrigger: {
                  trigger: el,
                  start: 'top 75%',
                  end: 'bottom 65%',
                  scrub: 0.35,
                  onEnter: () => setStickyFromPanel(i),
                  onEnterBack: () => setStickyFromPanel(i)
                }
              })
              .to(el, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out' })
              .to(el, { y: -8, duration: 0.6, ease: 'none' }, 0.15)
            })
          },

          // MÓVIL optimizado
          '(max-width: 900px)': () => {
            // El “sticky” NO se fija: actúa como encabezado descriptivo
            gsap.from(stickyRef.value, {
              autoAlpha: 0, y: 10, duration: 0.45, ease: 'power2.out',
              scrollTrigger: { trigger: stickyRef.value, start: 'top 92%', once: true }
            })

            // Animación ligera sin scrub (evita brincos)
            panelEls.value.forEach((el, i) => {
              gsap.fromTo(el,
                { autoAlpha: 0, y: 16 },
                {
                  autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out',
                  scrollTrigger: {
                    trigger: el,
                    start: 'top 92%',
                    once: true, // no re-animar al subir
                    onEnter: () => setStickyFromPanel(i),
                  }
                }
              )
            })
          }
        })

        // WHY
        gsap.from(whyRef.value.querySelectorAll('.why__card'), {
          autoAlpha: 0, y: 18, duration: 0.45, ease: 'power2.out', stagger: 0.07,
          scrollTrigger: { trigger: whyRef.value, start: 'top 88%', once: true }
        })
      }, root.value)
    })

    onBeforeUnmount(() => { ctx && ctx.revert() })

    return {
      root, heroRef, stickyRef, panelsRef, panelEls, whyRef,
      panels, why, currentKicker, currentTitle, currentLine
    }
  }
}
</script>

<style scoped>
/* ======= BRAND BLANCO ======= */
.mdx-white{
  --ink:#0f172a;
  --muted:#475569;
  --accent:#6425cc;
  --accent-ink:#ffffff;
  --card:#ffffff;
  --line:#e5e7eb;

  background:#ffffff;
  color:var(--ink);
  padding:48px 0 64px;
  font-family:'Poppins',system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial;
}

/* ======= HERO ======= */
.hero{ max-width:1100px; margin:0 auto 18px; padding:0 20px; text-align:center; }
.hero__eyebrow{ color:var(--muted); font-weight:800; font-size:13px; letter-spacing:.6px; margin:0 0 6px; text-transform:uppercase; }
.hero__title{ margin:0 0 10px; font-size:clamp(20px,5.2vw,38px); line-height:1.22; font-weight:900; color:#0b1220; }
.hero__sub{ color:#64748b; margin:0; }
.mark{ background:var(--accent); color:var(--accent-ink); padding:4px 10px; border-radius:12px; display:inline-block; }

/* ======= LAYOUT ======= */
.stage{
  max-width:1200px; margin:22px auto 0; display:grid; gap:18px;
  grid-template-columns: 1fr 1.4fr; align-items:start;
  padding:0 20px;
}

/* IZQ sticky */
.sticky__wrap{
  position:sticky; top:84px; padding:20px; border:1px solid var(--line); border-radius:16px;
  background:#fafafa;
}
.sticky__kicker{ color:#64748b; margin:0 0 8px; font-weight:800; letter-spacing:.3px; font-size:12px; text-transform:uppercase; }
.sticky__title{ margin:0 0 8px; font-size:clamp(20px,3.8vw,28px); line-height:1.25; font-weight:900; }
.badge{ background:#ede9fe; color:#1e1b4b; padding:4px 10px; border-radius:10px; }
.sticky__line{ color:#475569; margin:0 0 12px; }
.sticky__proof{ list-style:none; padding:0; margin:12px 0 0; display:grid; gap:6px; color:#334155; font-size:14px; }

/* DER panels */
.panels{ display:grid; gap:16px; }
.panel{
  background:var(--card);
  border:1px solid var(--line);
  border-radius:16px;
  overflow:hidden;
  box-shadow: 0 10px 18px rgba(2,6,23,.04);
}
.panel__content{ padding:22px 20px; }
.panel__eyebrow{ margin:0 0 6px; color:#6b7280; font-weight:800; font-size:12px; text-transform:uppercase; letter-spacing:.4px; }
.panel__title{ margin:0 0 8px; font-size:clamp(18px,3.6vw,26px); font-weight:900; color:#0b1220; line-height:1.25; }
.panel__text{ margin:0 0 12px; color:#334155; line-height:1.45; }
.panel__bullets{ margin:0; padding-left:18px; color:#1f2937; }
.panel__bullets li{ margin:6px 0; line-height:1.45; }
.panel__kpi{ margin-top:12px; display:flex; align-items:baseline; gap:8px; }
.panel__kpi .k{ font-size:24px; font-weight:900; color:#111827; }
.panel__kpi .klabel{ color:#475569; }

/* Temas */
.panel--purple{ border-color:#e9d5ff; }
.panel--blue{   border-color:#dbeafe; }
.panel--teal{   border-color:#ccfbf1; }
.panel--amber{  border-color:#fde68a; }
.panel--green{  border-color:#bbf7d0; }

/* WHY band */
.why{ max-width:1100px; margin:28px auto 0; padding:0 20px; }
.why__title{ margin:0 0 12px; font-size:clamp(20px,3.6vw,28px); font-weight:900; color:#0b1220; }
.why__grid{ display:grid; gap:14px; grid-template-columns:repeat(3,1fr); }
.why__card{
  background:#ffffff; border:1px solid var(--line); border-radius:14px; padding:16px;
  box-shadow:0 8px 16px rgba(2,6,23,.04);
}
.why__card h4{ margin:0 0 6px; font-size:16px; font-weight:900; color:#0b1220; }
.why__card p{ margin:0; color:#334155; font-size:14px; line-height:1.45; }

/* ======= Responsive ======= */
@media (max-width: 1000px){
  .stage{ grid-template-columns: 1fr; }
  .sticky__wrap{ position:relative; top:0; margin-bottom:8px; }
}
@media (max-width: 900px){
  .hero__title{ font-size:clamp(22px,6.8vw,30px); line-height:1.28; }
  .hero__sub{ font-size:15px; line-height:1.5; }
  .panel__content{ padding:16px 14px; }
  .panels{ gap:12px; }
}
@media (max-width: 680px){
  .why__grid{ grid-template-columns:1fr; }
  .mark{ border-radius:10px; padding:3px 8px; }
}
</style>
